import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Facebook, Instagram, Newspaper, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

const Nouvelles = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/news").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="nouvelles-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            Le blog du tournoi
          </span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-none">
            <span className="text-[#E60012]">Nouvelles</span>
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            Annonces, résultats marquants, instantanés du tournoi — tout ce qu'il se passe à la Coupe CH.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        {items === null && (
          <div className="text-center py-20 text-white/50 font-condensed uppercase tracking-[0.2em]">
            Chargement…
          </div>
        )}
        {items !== null && items.length === 0 && (
          <div className="text-center py-20 bg-[#111] border border-white/10">
            <Newspaper className="w-10 h-10 text-[#E60012] mx-auto mb-4" />
            <div className="font-display text-3xl uppercase">Aucune nouvelle pour le moment</div>
            <p className="mt-2 text-white/50">Restez à l'affût — les premières actualités arrivent bientôt.</p>
          </div>
        )}
        {items !== null && items.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((n, i) => (
              <motion.article
                key={n.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="bg-[#111] border border-white/10 hover:border-[#E60012] transition-all overflow-hidden clip-jersey flex flex-col group"
                data-testid={`news-card-${n.id}`}
              >
                <Link to={`/nouvelles/${n.id}`} className="contents">
                  {n.image_base64 && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={n.image_base64}
                        alt={n.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-xs font-condensed uppercase tracking-[0.25em] text-[#E60012]">
                      <Calendar className="w-3.5 h-3.5" /> {fmtDate(n.created_at)}
                    </div>
                    <h3 className="mt-2 font-display text-2xl uppercase leading-tight">{n.title}</h3>
                    {n.excerpt && <p className="mt-3 text-white/65 text-sm leading-relaxed flex-1">{n.excerpt}</p>}
                    <div className="mt-5 flex items-center gap-3">
                      {n.facebook_url && (
                        <a
                          href={n.facebook_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`news-fb-${n.id}`}
                          className="text-white/60 hover:text-[#1877F2] transition-colors"
                          aria-label="Facebook"
                        >
                          <Facebook className="w-5 h-5" />
                        </a>
                      )}
                      {n.instagram_url && (
                        <a
                          href={n.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`news-ig-${n.id}`}
                          className="text-white/60 hover:text-[#E1306C] transition-colors"
                          aria-label="Instagram"
                        >
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      <span className="ml-auto text-[#E60012] font-condensed uppercase tracking-[0.2em] text-xs flex items-center gap-1 group-hover:gap-2 transition-all">
                        Lire <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Nouvelles;
